/// <amd-module name="Wit/Filter/FilterValueProviders" />
import * as React from "react";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IListBoxItem } from "VSSUI/ListBox";
import { WorkItemClassificationNode, WorkItemTypeColorAndIcon } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { subtract } from "Wit/Common/Utilities/Array";
import { WiqlOperators, getLocalizedOperator } from "Wit/Common/WiqlOperators";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";
import { FilterValue, IBoardFilterDataSource, IFilterDataSource } from "Wit/Filter/FilterManager";
import * as Resources from "Wit/Filter/Resources";
import { union } from "Wit/Filter/Utilities/Array";
import { DataSourceFilterValueProvider, IWorkItemFilterItem, filterValueComparer, workItemFilterItemComparer } from "Wit/Filter/WorkItemFilter";
import {
    convertPotentialIdentityRefFromFieldValue,
    getFriendlyDistinctDisplayName,
    isIdentityRef,
    isWorkItemIdentityRef
} from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { IdentityDisplayMode, IdentityView } from "Wit/UI/IdentityView";
import { IWorkItemStateColorMetadataService, WorkItemStateColorMetadataServiceId, getIconDataObservable } from "Wit/UI/Services";
import { WorkItemState, WorkItemStateAndColor } from "Wit/UI/WorkItemState";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";

const MeFilterItem: IWorkItemFilterItem = {
    key: WiqlOperators.MacroMe,
    display: `${WiqlOperators.MacroStart}${Resources.Wiql_MacroMe}`,
    value: WiqlOperators.MacroMe
};

export const UnassignedFilterItem: IWorkItemFilterItem = {
    key: "_Unassigned_",
    display: Resources.AssignedToEmptyText,
    value: ""
};

/**
 * Assigned to data provider, takes all assigned to values and adds "Unassigned" as well as the '@Me' macro in the first position
 */
export class AssignedToFilterValueProvider extends DataSourceFilterValueProvider {
    constructor(pageContext: IVssPageContext, dataSource: IFilterDataSource, private _excludeMeFilter: boolean = false) {
        super(CoreFieldRefNames.AssignedTo, dataSource, pageContext);
    }

    public getItems(persistedValues?: FilterValue[]): Promise<IWorkItemFilterItem[]> | IWorkItemFilterItem[] {
        const values = super.getItems(persistedValues);

        if (values instanceof Promise) {
            return values.then(this.addMacros);
        } else {
            return this.addMacros(values);
        }
    }

    public getItemsForValues(values: FilterValue[]): Promise<IWorkItemFilterItem[]> {
        const results: Promise<IWorkItemFilterItem>[] = [];

        for (const value of values) {
            if (value === UnassignedFilterItem.value) {
                results.push(Promise.resolve(UnassignedFilterItem));
            } else if (value === WiqlOperators.MacroMe) {
                results.push(Promise.resolve(MeFilterItem));
            } else {
                results.push(Promise.resolve(super.getItemsForValues([value])).then(r => r[0]));
            }
        }

        return Promise.all(results);
    }

    public getListItem(pageContext: IVssPageContext, item: IWorkItemFilterItem): IListBoxItem {
        if ((item.key === MeFilterItem.key || item.key === UnassignedFilterItem.key) && typeof item.value === "string") {
            return super.getListItem(pageContext, item);
        }

        const { key, value, display } = item;
        const identityRef = convertPotentialIdentityRefFromFieldValue(pageContext, value ?? key ?? display, true);
        const id = key ?? display;
        return {
            id: id,
            text: display,
            iconProps: {
                render: () => React.createElement(IdentityView, { viewMode: IdentityDisplayMode.AvatarOnly, identityRef })
            }
        };
    }

    protected transformResults(uniqueValues: FilterValue[], persistedValues?: FilterValue[]): IWorkItemFilterItem[] {
        // copy values array before union
        let values = uniqueValues.slice(0);
        if (persistedValues) {
            persistedValues = persistedValues.filter(v => !!v);

            values = union(values, this._filterDistinctNames(values, persistedValues), filterValueComparer);
        }
        return values.map(this.transformResult, this);
    }

    /**
     * Used to prevent duplicate names from showing in Assigned To filter field. Values and persistedValues may have the same user in it,
     * but persistedValues had just the displayName as a string whereas values had the full WorkItemIdentityRef object in it. Union does not handle this case.
     * This function removes the string from persistedValues if the user already exists in values, since the WorkItemIdentityRef object has more necessary information about the user.
     */
    private _filterDistinctNames(values: FilterValue[], persistedValues: FilterValue[]): FilterValue[] {
        const uniquePersistedValues = new Set(persistedValues);

        for (const value of values) {
            let distinctDisplayName: string;
            if (isWorkItemIdentityRef(value)) {
                distinctDisplayName = value.distinctDisplayName;
            } else if (isIdentityRef(value)) {
                distinctDisplayName = getFriendlyDistinctDisplayName(value);
            }

            if (distinctDisplayName && uniquePersistedValues.has(distinctDisplayName)) {
                uniquePersistedValues.delete(distinctDisplayName);
            }
        }
        return Array.from(uniquePersistedValues);
    }

    private addMacros = (values: IWorkItemFilterItem[]): IWorkItemFilterItem[] => {
        const macros = [MeFilterItem, UnassignedFilterItem];
        // make sure there are no macro dupes that came from persisted values.
        values = subtract(values, macros, workItemFilterItemComparer);

        if (this._excludeMeFilter) {
            macros.shift(); // remove MeFilterItem
        }
        return macros.concat(values);
    }
}

/**
 * Helper function used when retrieving state/work item type information.  For a given field value, finds that value in a row in the provided data
 * set.  If that row is associated with the 'current' project then that project/work item type pair is returned,
 * otherwise it will keep looking for a match on the 'current' project.  If no match for 'current' project is found, it will return the project/type
 * for the first instance it encountered. If nothing found - fallback to the current project and unknown work item type.
 * @param fieldName Field which will contain 'value'
 * @param value Value to search for in the data
 */
function getWorkItemTypeFor(projectName: string, dataSource: IFilterDataSource, fieldName: string, value: string): IProjectAndType {
    // Rip thru the data provider to any work item type with the specified values

    let currentValue: IProjectAndType;
    const ids = dataSource.getIds();

    for (const id of ids) {
        const columnValue = dataSource.getValue(id, fieldName);

        if (value === columnValue) {
            const workItemProjectName = dataSource.getValue(id, CoreFieldRefNames.TeamProject) || projectName;
            const workItemTypeName = dataSource.getValue(id, CoreFieldRefNames.WorkItemType);
            const thisValue: IProjectAndType = {
                projectName: workItemProjectName,
                workItemType: workItemTypeName
            };

            // If it matches the project name and the value then this is the one we want
            // Otherwise just record it and if we don't find a better match return that one.
            if (localeIgnoreCaseComparer(workItemProjectName, projectName) === 0) {
                currentValue = thisValue;
                break;
            }

            if (!currentValue) {
                currentValue = thisValue;
            }
        }
    }

    // default to the current project in case there are no items that match the filter in the dataSource.
    if (!currentValue) {
        currentValue = { projectName };
    }

    return currentValue;
}

/**
 * Internal interface used as a return value.
 */
interface IProjectAndType {
    projectName: string;

    workItemType?: string;
}

/**
 * Work item state value provider
 */
export class StateFilterValueProvider extends DataSourceFilterValueProvider {
    constructor(private defaultProjectNameOrId: string, dataSource: IFilterDataSource) {
        super(CoreFieldRefNames.State, dataSource);
    }

    public getListItem(pageContext: IVssPageContext, item: IWorkItemFilterItem): IListBoxItem {
        const stateName = item.value as string;
        const workItemTypeData = getWorkItemTypeFor(this.defaultProjectNameOrId, this.dataSource, CoreFieldRefNames.State, stateName);

        const stateAndColor: IObservableValue<WorkItemStateAndColor> = new ObservableValue({
            fill: null,
            name: stateName
        });

        const workItemStateColorMetadataService = pageContext.getService<IWorkItemStateColorMetadataService>(WorkItemStateColorMetadataServiceId);
        if (workItemTypeData) {
            const result: WorkItemStateAndColor | Promise<WorkItemStateAndColor> = workItemStateColorMetadataService.getWorkItemStateColor(
                workItemTypeData.projectName,
                workItemTypeData.workItemType,
                stateName
            );

            if (result instanceof Promise) {
                result.then(c => {
                    (stateAndColor as IObservableValue<WorkItemStateAndColor>).value = c;
                });
            } else {
                stateAndColor.value = result;
            }
        }

        return {
            id: stateName,
            text: stateName,
            iconProps: {
                render: () =>
                    React.createElement(WorkItemState, {
                        className: "margin-right-4",
                        data: stateAndColor,
                        hideText: true
                    })
            }
        };
    }
}

export class WorkItemTypeFilterValueProvider extends DataSourceFilterValueProvider {
    private _colorAndIconMap?: { [name: string]: WorkItemTypeColorAndIcon };

    constructor(private projectName: string, dataSource: IFilterDataSource, colorAndIconMap?: { [name: string]: WorkItemTypeColorAndIcon }) {
        super(CoreFieldRefNames.WorkItemType, dataSource, null);
        this._colorAndIconMap = colorAndIconMap;
    }

    // TODO see if this pagecontext is passed anywhere as a parameter
    public getListItem(pageContext: IVssPageContext, item: IWorkItemFilterItem): IListBoxItem {
        const workItemTypeName = item.value as string;
        const workItemTypeData = getWorkItemTypeFor(this.projectName, this.dataSource, CoreFieldRefNames.WorkItemType, workItemTypeName);

        // Default color and icon
        let colorAndIcon: WorkItemTypeColorAndIcon | ObservableValue<WorkItemTypeColorAndIcon> = {
            color: null,
            icon: null,
            workItemTypeName,
            isDisabled: false
        };
        if (this._colorAndIconMap && this._colorAndIconMap[workItemTypeName]) {
            colorAndIcon = this._colorAndIconMap[workItemTypeName];
        } else if (workItemTypeData && workItemTypeData.projectName) {
            colorAndIcon = getIconDataObservable(workItemTypeData.projectName, workItemTypeName, pageContext);
        }

        return {
            id: workItemTypeName,
            text: workItemTypeName,
            iconProps: {
                render: () =>
                    React.createElement(WorkItemTypeIcon, {
                        className: "margin-right-4",
                        data: colorAndIcon
                    })
            }
        };
    }
}

export const UnparentedFilterItem: IWorkItemFilterItem = {
    key: "_Unparented_",
    display: Resources.UnparentedText,
    value: ""
};

export class ParentItemFilterValueProvider extends DataSourceFilterValueProvider {
    protected dataSource: IBoardFilterDataSource;

    constructor(pageContext: IVssPageContext, dataSource: IBoardFilterDataSource) {
        super(CoreFieldRefNames.Parent, dataSource, pageContext);
    }

    public addUnparentedFilter(values: IWorkItemFilterItem[]): IWorkItemFilterItem[] {
        if (values.filter(value => value.key === UnparentedFilterItem.key).length > 0) {
            return values;
        }

        return [UnparentedFilterItem].concat(values);
    }

    public getItems(persistedValues?: FilterValue[]): Promise<IWorkItemFilterItem[]> | IWorkItemFilterItem[] {
        const parentOptions = this.dataSource.getParentItems().map(v => {
            const normalizedId = String(v.id);
            return {
                key: normalizedId,
                display: v.title,
                value: normalizedId
            } as IWorkItemFilterItem;
        });

        return this.addUnparentedFilter(parentOptions);
    }
}

const CurrentIterationFilterItem = {
    key: WiqlOperators.MacroCurrentIteration,
    value: WiqlOperators.MacroCurrentIteration,
    display: getLocalizedOperator(WiqlOperators.MacroCurrentIteration)
};

export class IterationPathFilterValueProvider extends DataSourceFilterValueProvider {
    private addCurrentIterationMacro: boolean;
    private projectId: string;

    constructor(pageContext: IVssPageContext, projectId: string, dataSource: IBoardFilterDataSource, addCurrentIterationMacro: boolean) {
        super(CoreFieldRefNames.IterationPath, dataSource, pageContext);
        this.addCurrentIterationMacro = addCurrentIterationMacro;
        this.projectId = projectId;
    }

    public async getItems(persistedValues?: string[]): Promise<IWorkItemFilterItem[]> {
        let iterationPathValues = await this.getIterationPathValues();
        const pathsToLoad = [...iterationPathValues, ...(persistedValues?.filter(v => v === CurrentIterationFilterItem.key) ?? [])].map(v =>
            v.toString()
        );
        const pathToNodeMap = await this.getPathToNodeMap(pathsToLoad);
        if (persistedValues) {
            // make sure there are no macro dupes that came from persisted values.
            persistedValues = subtract(persistedValues, [CurrentIterationFilterItem.key], localeIgnoreCaseComparer);
            // filter out the persisted iteration that is already deleted.
            persistedValues = persistedValues.filter(persistedValue => !!pathToNodeMap.get(persistedValue.toString()));
            iterationPathValues = union(iterationPathValues, persistedValues, localeIgnoreCaseComparer);
        }

        const pathsAndNodes: {
            path: string;
            node: WorkItemClassificationNode;
        }[] = [];
        iterationPathValues.forEach(async iterationPathValue => {
            //  Some iteration paths might not exist - e.g. child tasks from a different project.
            const node = pathToNodeMap.get(iterationPathValue.toString());
            if (node) {
                pathsAndNodes.push({
                    path: iterationPathValue.toString(),
                    node
                });
            }
        });

        pathsAndNodes.sort((a, b) => {
            const nodeA = a.node;
            const nodeB = b.node;

            // Sort by start date first
            const dateComparison = compareStartDates(nodeA, nodeB);
            if (dateComparison !== 0) {
                return dateComparison;
            }

            // Fall back to name
            const nameComparison = localeIgnoreCaseComparer(nodeA.name, nodeB.name);
            if (nameComparison !== 0) {
                return nameComparison;
            }

            return nodeA.id - nodeB.id;
        });

        const filterItems = pathsAndNodes.map(n => ({
            key: n.path,
            value: n.path,
            display: n.node.name
        }));

        if (this.addCurrentIterationMacro) {
            filterItems.unshift(CurrentIterationFilterItem);
        }

        return filterItems;
    }

    public async getItemsForValues(values: FilterValue[]): Promise<IWorkItemFilterItem[]> {
        const nodeService = this.pageContext!.getService<IWorkItemNodeService>("IWorkItemNodeService");
        let filterItems: IWorkItemFilterItem[] = [];
        values.forEach((value: string) => {
            if (value === WiqlOperators.MacroCurrentIteration) {
                filterItems.push(CurrentIterationFilterItem);
            } else {
                const node = nodeService.findIterationNodeByPath(this.projectId, value.toString());
                if (node) {
                    filterItems.push(this.transformNode(value, node));
                }
            }
        });

        return filterItems;
    }

    private async getIterationPathValues(): Promise<FilterValue[]> {
        return await this.dataSource.getUniqueValues(CoreFieldRefNames.IterationPath);
    }

    private async getPathToNodeMap(paths: string[]): Promise<Map<string, WorkItemClassificationNode | undefined>> {
        const nodeService = this.pageContext!.getService<IWorkItemNodeService>("IWorkItemNodeService");
        const map = new Map<string, WorkItemClassificationNode | undefined>();
        for (const path of paths) {
            const node = await nodeService.findIterationNodeByPath(this.projectId, path);
            map.set(path, node);
        }

        return map;
    }

    private transformNode(path: string, node: any): IWorkItemFilterItem {
        return {
            key: path,
            value: path,
            display: node.name
        };
    }
}

function compareStartDates(nodeA: WorkItemClassificationNode, nodeB: WorkItemClassificationNode): number {
    const startA = getStartDate(nodeA);
    const startB = getStartDate(nodeB);

    if (startA instanceof Date && startB instanceof Date) {
        return startA.getTime() - startB.getTime();
    }

    if (startA instanceof Date) {
        return 1;
    }

    if (startB instanceof Date) {
        return -1;
    }

    return 0;
}

function getStartDate(node: WorkItemClassificationNode): Date | undefined {
    return node.attributes?.["startDate"];
}
